import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation data
const translations = {
  EN: {
    // Navigation
    'nav.funding_options': 'Funding Options',
    'nav.how_it_works': 'How it Works',
    'nav.about_us': 'About Us',
    'nav.blog': 'Blog',
    'nav.faq': 'FAQ',
    'nav.login': 'Login',
    'nav.language': 'Language',
    
    // Hero Section
    'hero.title': 'Empowering Dreams Through Smart Funding',
    'hero.subtitle': 'Get the financial support you need to turn your business ideas into reality',
    'hero.apply_now': 'Apply Now',
    'hero.learn_more': 'Learn More',
    
    // Application Form
    'form.full_name': 'Full Name',
    'form.email': 'Email Address',
    'form.phone': 'Phone Number',
    'form.business_name': 'Business Name',
    'form.business_category': 'Business Category',
    'form.funding_amount': 'Funding Amount Required',
    'form.business_description': 'Business Description',
    'form.submit': 'Submit Application',
    'form.required': 'This field is required',
    'form.invalid_email': 'Please enter a valid email address',
    'form.invalid_phone': 'Please enter a valid phone number',
    
    // Application Periods Section
    'periods.title': 'Application Periods & Results',
    'periods.current_period': '📢 Current Application Period',
    'periods.active': 'Active',
    'periods.start_date': 'Start Date:',
    'periods.end_date': 'End Date:',
    'periods.apply_now': 'Apply Now',
    'periods.latest_results': '🏆 Latest Funding Results',
    'periods.congratulations': 'Congratulations to our recent funding recipients!',
    'periods.funding_received': 'Funding Received:',
    'periods.no_active_period': 'No Active Application Period',
    'periods.no_period_message': 'Currently, there are no active application periods. Please check back later for new opportunities.',
    
    // Success Stories
    'stories.title': 'Success Stories',
    'stories.subtitle': 'Real stories from entrepreneurs who achieved their dreams',
    
    // About Us
    'about.title': 'About Vikas Fund',
    'about.subtitle': 'Empowering entrepreneurs across India',
    'about.description': 'We are committed to supporting innovative business ideas and helping entrepreneurs turn their dreams into reality.',
    'about.clients_served': 'Clients Served',
    'about.funding_disbursed': 'Funding Disbursed',
    'about.feature_1_title': 'Innovative Solutions',
    'about.feature_1_desc': 'Cutting-edge financial products designed for modern businesses',
    'about.feature_2_title': 'Quick Processing',
    'about.feature_2_desc': 'Fast approval and disbursement within 24-48 hours',
    'about.feature_3_title': 'Secure & Transparent',
    'about.feature_3_desc': 'Your data and transactions are protected with bank-level security',
    'about.learn_more': 'Learn More',
    'about.contact_us': 'Contact Us',
    
    // Footer
    'footer.contact': 'Contact Us',
    'footer.address': 'Address',
    'footer.phone': 'Phone',
    'footer.email': 'Email',
    'footer.follow_us': 'Follow Us',
    'footer.copyright': '© 2024 Vikas Fund. All rights reserved.',
    
    // Admin Panel
    'admin.dashboard': 'Dashboard',
    'admin.applications': 'Applications',
    'admin.success_stories': 'Success Stories',
    'admin.funding_options': 'Funding Options',
    'admin.blog_posts': 'Blog Posts',
    'admin.faqs': 'FAQs',
    'admin.settings': 'Settings',
    'admin.masters': 'Masters',
    'admin.country_master': 'Country Master',
    'admin.state_master': 'State Master',
    'admin.city_master': 'City Master',
    'admin.business_category': 'Business Category',
    'admin.application_periods': 'Application Periods',
    'admin.funding_results': 'Funding Results',
    'admin.logout': 'Logout',
    
    // Common
    'common.add': 'Add',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.created_at': 'Created At',
    'common.updated_at': 'Updated At',
    'common.loading': 'Loading...',
    'common.no_data': 'No data available',
    'common.success': 'Success',
    'common.error': 'Error',
    'common.warning': 'Warning',
    'common.info': 'Information'
  },
  
  HI: {
    // Navigation
    'nav.funding_options': 'फंडिंग विकल्प',
    'nav.how_it_works': 'कैसे काम करता है',
    'nav.about_us': 'हमारे बारे में',
    'nav.blog': 'ब्लॉग',
    'nav.faq': 'सामान्य प्रश्न',
    'nav.login': 'लॉगिन',
    'nav.language': 'भाषा',
    
    // Hero Section
    'hero.title': 'स्मार्ट फंडिंग के माध्यम से सपनों को साकार करें',
    'hero.subtitle': 'अपने व्यवसाय के विचारों को वास्तविकता में बदलने के लिए वित्तीय सहायता प्राप्त करें',
    'hero.apply_now': 'अभी आवेदन करें',
    'hero.learn_more': 'और जानें',
    
    // Application Form
    'form.full_name': 'पूरा नाम',
    'form.email': 'ईमेल पता',
    'form.phone': 'फोन नंबर',
    'form.business_name': 'व्यवसाय का नाम',
    'form.business_category': 'व्यवसाय श्रेणी',
    'form.funding_amount': 'आवश्यक फंडिंग राशि',
    'form.business_description': 'व्यवसाय का विवरण',
    'form.submit': 'आवेदन जमा करें',
    'form.required': 'यह फील्ड आवश्यक है',
    'form.invalid_email': 'कृपया एक वैध ईमेल पता दर्ज करें',
    'form.invalid_phone': 'कृपया एक वैध फोन नंबर दर्ज करें',
    
    // Application Periods Section
    'periods.title': 'आवेदन अवधि और परिणाम',
    'periods.current_period': '📢 वर्तमान आवेदन अवधि',
    'periods.active': 'सक्रिय',
    'periods.start_date': 'प्रारंभ तिथि:',
    'periods.end_date': 'समाप्ति तिथि:',
    'periods.apply_now': 'अभी आवेदन करें',
    'periods.latest_results': '🏆 नवीनतम फंडिंग परिणाम',
    'periods.congratulations': 'हमारे हाल के फंडिंग प्राप्तकर्ताओं को बधाई!',
    'periods.funding_received': 'प्राप्त फंडिंग:',
    'periods.no_active_period': 'कोई सक्रिय आवेदन अवधि नहीं',
    'periods.no_period_message': 'वर्तमान में कोई सक्रिय आवेदन अवधि नहीं है। कृपया नए अवसरों के लिए बाद में जांचें।',
    
    // Success Stories
    'stories.title': 'सफलता की कहानियां',
    'stories.subtitle': 'उद्यमियों की वास्तविक कहानियां जिन्होंने अपने सपनों को साकार किया',
    
    // About Us
    'about.title': 'विकास फंड के बारे में',
    'about.subtitle': 'पूरे भारत में उद्यमियों को सशक्त बनाना',
    'about.description': 'हम नवीन व्यवसाय विचारों का समर्थन करने और उद्यमियों को उनके सपनों को वास्तविकता में बदलने में मदद करने के लिए प्रतिबद्ध हैं।',
    'about.clients_served': 'ग्राहकों की सेवा',
    'about.funding_disbursed': 'वितरित फंडिंग',
    'about.feature_1_title': 'नवीन समाधान',
    'about.feature_1_desc': 'आधुनिक व्यवसायों के लिए डिज़ाइन किए गए अत्याधुनिक वित्तीय उत्पाद',
    'about.feature_2_title': 'त्वरित प्रसंस्करण',
    'about.feature_2_desc': '24-48 घंटों के भीतर त्वरित स्वीकृति और वितरण',
    'about.feature_3_title': 'सुरक्षित और पारदर्शी',
    'about.feature_3_desc': 'आपका डेटा और लेन-देन बैंक-स्तरीय सुरक्षा के साथ संरक्षित है',
    'about.learn_more': 'और जानें',
    'about.contact_us': 'संपर्क करें',
    
    // Footer
    'footer.contact': 'संपर्क करें',
    'footer.address': 'पता',
    'footer.phone': 'फोन',
    'footer.email': 'ईमेल',
    'footer.follow_us': 'हमें फॉलो करें',
    'footer.copyright': '© 2024 विकास फंड। सर्वाधिकार सुरक्षित।',
    
    // Admin Panel
    'admin.dashboard': 'डैशबोर्ड',
    'admin.applications': 'आवेदन',
    'admin.success_stories': 'सफलता की कहानियां',
    'admin.funding_options': 'फंडिंग विकल्प',
    'admin.blog_posts': 'ब्लॉग पोस्ट',
    'admin.faqs': 'सामान्य प्रश्न',
    'admin.settings': 'सेटिंग्स',
    'admin.masters': 'मास्टर',
    'admin.country_master': 'देश मास्टर',
    'admin.state_master': 'राज्य मास्टर',
    'admin.city_master': 'शहर मास्टर',
    'admin.business_category': 'व्यवसाय श्रेणी',
    'admin.application_periods': 'आवेदन अवधि',
    'admin.funding_results': 'फंडिंग परिणाम',
    'admin.logout': 'लॉगआउट',
    
    // Common
    'common.add': 'जोड़ें',
    'common.edit': 'संपादित करें',
    'common.delete': 'हटाएं',
    'common.save': 'सहेजें',
    'common.cancel': 'रद्द करें',
    'common.search': 'खोजें',
    'common.filter': 'फ़िल्टर',
    'common.actions': 'कार्रवाई',
    'common.status': 'स्थिति',
    'common.created_at': 'बनाया गया',
    'common.updated_at': 'अपडेट किया गया',
    'common.loading': 'लोड हो रहा है...',
    'common.no_data': 'कोई डेटा उपलब्ध नहीं',
    'common.success': 'सफल',
    'common.error': 'त्रुटि',
    'common.warning': 'चेतावनी',
    'common.info': 'जानकारी'
  },
  
  GU: {
    // Navigation
    'nav.funding_options': 'ફંડિંગ વિકલ્પો',
    'nav.how_it_works': 'કેવી રીતે કામ કરે છે',
    'nav.about_us': 'અમારા વિશે',
    'nav.blog': 'બ્લોગ',
    'nav.faq': 'વારંવાર પૂછાતા પ્રશ્નો',
    'nav.login': 'લૉગિન',
    'nav.language': 'ભાષા',
    
    // Hero Section
    'hero.title': 'સ્માર્ટ ફંડિંગ દ્વારા સ્વપ્નોને સાકાર કરો',
    'hero.subtitle': 'તમારા વ્યવસાયના વિચારોને વાસ્તવિકતામાં ફેરવવા માટે નાણાકીય સહાય મેળવો',
    'hero.apply_now': 'હવે અરજી કરો',
    'hero.learn_more': 'વધુ જાણો',
    
    // Application Form
    'form.full_name': 'પૂરું નામ',
    'form.email': 'ઈમેલ સરનામું',
    'form.phone': 'ફોન નંબર',
    'form.business_name': 'વ્યવસાયનું નામ',
    'form.business_category': 'વ્યવસાય શ્રેણી',
    'form.funding_amount': 'જરૂરી ફંડિંગ રકમ',
    'form.business_description': 'વ્યવસાયનું વર્ણન',
    'form.submit': 'અરજી સબમિટ કરો',
    'form.required': 'આ ફીલ્ડ જરૂરી છે',
    'form.invalid_email': 'કૃપા કરીને માન્ય ઈમેલ સરનામું દાખલ કરો',
    'form.invalid_phone': 'કૃપા કરીને માન્ય ફોન નંબર દાખલ કરો',
    
    // Application Periods Section
    'periods.title': 'અરજી સમયગાળો અને પરિણામો',
    'periods.current_period': '📢 વર્તમાન અરજી સમયગાળો',
    'periods.active': 'સક્રિય',
    'periods.start_date': 'પ્રારંભ તારીખ:',
    'periods.end_date': 'સમાપ્તિ તારીખ:',
    'periods.apply_now': 'હવે અરજી કરો',
    'periods.latest_results': '🏆 તાજેતરના ફંડિંગ પરિણામો',
    'periods.congratulations': 'અમારા તાજેતરના ફંડિંગ પ્રાપ્તકર્તાઓને અભિનંદન!',
    'periods.funding_received': 'પ્રાપ્ત ફંડિંગ:',
    'periods.no_active_period': 'કોઈ સક્રિય અરજી સમયગાળો નથી',
    'periods.no_period_message': 'હાલમાં કોઈ સક્રિય અરજી સમયગાળો નથી. કૃપા કરીને નવા તકો માટે પછીથી તપાસો.',
    
    // Success Stories
    'stories.title': 'સફળતાની વાર્તાઓ',
    'stories.subtitle': 'ઉદ્યોગપતિઓની વાસ્તવિક વાર્તાઓ જેમણે પોતાના સ્વપ્નો સાકાર કર્યા',
    
    // About Us
    'about.title': 'વિકાસ ફંડ વિશે',
    'about.subtitle': 'ભારત ભરમાં ઉદ્યોગપતિઓને સશક્ત બનાવવા',
    'about.description': 'અમે નવીન વ્યવસાય વિચારોને સમર્થન આપવા અને ઉદ્યોગપતિઓને તેમના સ્વપ્નોને વાસ્તવિકતામાં ફેરવવામાં મદદ કરવા માટે પ્રતિબદ્ધ છીએ.',
    
    // Footer
    'footer.contact': 'અમારો સંપર્ક કરો',
    'footer.address': 'સરનામું',
    'footer.phone': 'ફોન',
    'footer.email': 'ઈમેલ',
    'footer.follow_us': 'અમને ફોલો કરો',
    'footer.copyright': '© 2024 વિકાસ ફંડ. બધા અધિકારો સુરક્ષિત.',
    
    // Admin Panel
    'admin.dashboard': 'ડેશબોર્ડ',
    'admin.applications': 'અરજીઓ',
    'admin.success_stories': 'સફળતાની વાર્તાઓ',
    'admin.funding_options': 'ફંડિંગ વિકલ્પો',
    'admin.blog_posts': 'બ્લોગ પોસ્ટ્સ',
    'admin.faqs': 'વારંવાર પૂછાતા પ્રશ્નો',
    'admin.settings': 'સેટિંગ્સ',
    'admin.masters': 'માસ્ટર',
    'admin.country_master': 'દેશ માસ્ટર',
    'admin.state_master': 'રાજ્ય માસ્ટર',
    'admin.city_master': 'શહેર માસ્ટર',
    'admin.business_category': 'વ્યવસાય શ્રેણી',
    'admin.application_periods': 'અરજી સમયગાળા',
    'admin.funding_results': 'ફંડિંગ પરિણામો',
    'admin.logout': 'લૉગઆઉટ',
    
    // Common
    'common.add': 'ઉમેરો',
    'common.edit': 'સંપાદિત કરો',
    'common.delete': 'કાઢી નાખો',
    'common.save': 'સાચવો',
    'common.cancel': 'રદ કરો',
    'common.search': 'શોધો',
    'common.filter': 'ફિલ્ટર',
    'common.actions': 'ક્રિયાઓ',
    'common.status': 'સ્થિતિ',
    'common.created_at': 'બનાવ્યું',
    'common.updated_at': 'અપડેટ કર્યું',
    'common.loading': 'લોડ થઈ રહ્યું છે...',
    'common.no_data': 'કોઈ ડેટા ઉપલબ્ધ નથી',
    'common.success': 'સફળ',
    'common.error': 'ભૂલ',
    'common.warning': 'ચેતવણી',
    'common.info': 'માહિતી'
  },
  
  MR: {
    // Navigation
    'nav.funding_options': 'फंडिंग पर्याय',
    'nav.how_it_works': 'कसे काम करते',
    'nav.about_us': 'आमच्याबद्दल',
    'nav.blog': 'ब्लॉग',
    'nav.faq': 'सामान्य प्रश्न',
    'nav.login': 'लॉगिन',
    'nav.language': 'भाषा',
    
    // Hero Section
    'hero.title': 'स्मार्ट फंडिंगद्वारे स्वप्ने साकार करा',
    'hero.subtitle': 'तुमच्या व्यवसाय कल्पनांना वास्तवात आणण्यासाठी आर्थिक सहाय्य मिळवा',
    'hero.apply_now': 'आता अर्ज करा',
    'hero.learn_more': 'अधिक जाणून घ्या',
    
    // Application Form
    'form.full_name': 'पूर्ण नाव',
    'form.email': 'ईमेल पत्ता',
    'form.phone': 'फोन नंबर',
    'form.business_name': 'व्यवसायाचे नाव',
    'form.business_category': 'व्यवसाय श्रेणी',
    'form.funding_amount': 'आवश्यक फंडिंग रक्कम',
    'form.business_description': 'व्यवसायाचे वर्णन',
    'form.submit': 'अर्ज सबमिट करा',
    'form.required': 'हे फील्ड आवश्यक आहे',
    'form.invalid_email': 'कृपया वैध ईमेल पत्ता प्रविष्ट करा',
    'form.invalid_phone': 'कृपया वैध फोन नंबर प्रविष्ट करा',
    
    // Application Periods Section
    'periods.title': 'अर्ज कालावधी आणि निकाल',
    'periods.current_period': '📢 सध्याचा अर्ज कालावधी',
    'periods.active': 'सक्रिय',
    'periods.start_date': 'प्रारंभ तारीख:',
    'periods.end_date': 'समाप्ती तारीख:',
    'periods.apply_now': 'आता अर्ज करा',
    'periods.latest_results': '🏆 नवीनतम फंडिंग निकाल',
    'periods.congratulations': 'आमच्या अलीकडील फंडिंग प्राप्तकर्त्यांना अभिनंदन!',
    'periods.funding_received': 'प्राप्त फंडिंग:',
    'periods.no_active_period': 'कोणताही सक्रिय अर्ज कालावधी नाही',
    'periods.no_period_message': 'सध्या कोणताही सक्रिय अर्ज कालावधी नाही. कृपया नवीन संधींसाठी नंतर तपासा.',
    
    // Success Stories
    'stories.title': 'यशाच्या कथा',
    'stories.subtitle': 'उद्योजकांच्या वास्तविक कथा ज्यांनी त्यांचे स्वप्न साकार केले',
    
    // About Us
    'about.title': 'विकास फंड बद्दल',
    'about.subtitle': 'संपूर्ण भारतातील उद्योजकांना सक्षम करणे',
    'about.description': 'आम्ही नाविन्यपूर्ण व्यवसाय कल्पनांना पाठिंबा देण्यास आणि उद्योजकांना त्यांचे स्वप्न वास्तवात आणण्यात मदत करण्यास वचनबद्ध आहोत.',
    
    // Footer
    'footer.contact': 'आमच्याशी संपर्क साधा',
    'footer.address': 'पत्ता',
    'footer.phone': 'फोन',
    'footer.email': 'ईमेल',
    'footer.follow_us': 'आम्हाला फॉलो करा',
    'footer.copyright': '© 2024 विकास फंड. सर्व हक्क राखीव.',
    
    // Admin Panel
    'admin.dashboard': 'डॅशबोर्ड',
    'admin.applications': 'अर्ज',
    'admin.success_stories': 'यशाच्या कथा',
    'admin.funding_options': 'फंडिंग पर्याय',
    'admin.blog_posts': 'ब्लॉग पोस्ट',
    'admin.faqs': 'सामान्य प्रश्न',
    'admin.settings': 'सेटिंग्ज',
    'admin.masters': 'मास्टर',
    'admin.country_master': 'देश मास्टर',
    'admin.state_master': 'राज्य मास्टर',
    'admin.city_master': 'शहर मास्टर',
    'admin.business_category': 'व्यवसाय श्रेणी',
    'admin.application_periods': 'अर्ज कालावधी',
    'admin.funding_results': 'फंडिंग निकाल',
    'admin.logout': 'लॉगआउट',
    
    // Common
    'common.add': 'जोडा',
    'common.edit': 'संपादित करा',
    'common.delete': 'हटवा',
    'common.save': 'जतन करा',
    'common.cancel': 'रद्द करा',
    'common.search': 'शोधा',
    'common.filter': 'फिल्टर',
    'common.actions': 'कृती',
    'common.status': 'स्थिती',
    'common.created_at': 'तयार केले',
    'common.updated_at': 'अपडेट केले',
    'common.loading': 'लोड होत आहे...',
    'common.no_data': 'कोणताही डेटा उपलब्ध नाही',
    'common.success': 'यशस्वी',
    'common.error': 'त्रुटी',
    'common.warning': 'चेतावणी',
    'common.info': 'माहिती'
  },
  
  BN: {
    // Navigation
    'nav.funding_options': 'ফান্ডিং অপশন',
    'nav.how_it_works': 'কিভাবে কাজ করে',
    'nav.about_us': 'আমাদের সম্পর্কে',
    'nav.blog': 'ব্লগ',
    'nav.faq': 'সাধারণ প্রশ্ন',
    'nav.login': 'লগইন',
    'nav.language': 'ভাষা',
    
    // Hero Section
    'hero.title': 'স্মার্ট ফান্ডিং দিয়ে স্বপ্ন বাস্তবায়ন করুন',
    'hero.subtitle': 'আপনার ব্যবসায়িক ধারণাগুলিকে বাস্তবে রূপান্তর করতে আর্থিক সহায়তা পান',
    'hero.apply_now': 'এখন আবেদন করুন',
    'hero.learn_more': 'আরও জানুন',
    
    // Application Form
    'form.full_name': 'পূর্ণ নাম',
    'form.email': 'ইমেইল ঠিকানা',
    'form.phone': 'ফোন নম্বর',
    'form.business_name': 'ব্যবসার নাম',
    'form.business_category': 'ব্যবসার বিভাগ',
    'form.funding_amount': 'প্রয়োজনীয় ফান্ডিং পরিমাণ',
    'form.business_description': 'ব্যবসার বিবরণ',
    'form.submit': 'আবেদন জমা দিন',
    'form.required': 'এই ক্ষেত্রটি প্রয়োজনীয়',
    'form.invalid_email': 'অনুগ্রহ করে একটি বৈধ ইমেইল ঠিকানা লিখুন',
    'form.invalid_phone': 'অনুগ্রহ করে একটি বৈধ ফোন নম্বর লিখুন',
    
    // Application Periods Section
    'periods.title': 'আবেদনের সময়কাল এবং ফলাফল',
    'periods.current_period': '📢 বর্তমান আবেদনের সময়কাল',
    'periods.active': 'সক্রিয়',
    'periods.start_date': 'শুরুর তারিখ:',
    'periods.end_date': 'শেষের তারিখ:',
    'periods.apply_now': 'এখন আবেদন করুন',
    'periods.latest_results': '🏆 সর্বশেষ ফান্ডিং ফলাফল',
    'periods.congratulations': 'আমাদের সাম্প্রতিক ফান্ডিং প্রাপকদের অভিনন্দন!',
    'periods.funding_received': 'প্রাপ্ত ফান্ডিং:',
    'periods.no_active_period': 'কোন সক্রিয় আবেদনের সময়কাল নেই',
    'periods.no_period_message': 'বর্তমানে কোন সক্রিয় আবেদনের সময়কাল নেই। অনুগ্রহ করে নতুন সুযোগের জন্য পরে দেখুন।',
    
    // Success Stories
    'stories.title': 'সাফল্যের গল্প',
    'stories.subtitle': 'উদ্যোক্তাদের বাস্তব গল্প যারা তাদের স্বপ্ন বাস্তবায়ন করেছেন',
    
    // About Us
    'about.title': 'বিকাশ ফান্ড সম্পর্কে',
    'about.subtitle': 'সম্পূর্ণ ভারত জুড়ে উদ্যোক্তাদের ক্ষমতায়ন',
    'about.description': 'আমরা উদ্ভাবনী ব্যবসায়িক ধারণাকে সমর্থন করতে এবং উদ্যোক্তাদের তাদের স্বপ্ন বাস্তবে রূপান্তর করতে সাহায্য করতে প্রতিশ্রুতিবদ্ধ।',
    
    // Footer
    'footer.contact': 'আমাদের সাথে যোগাযোগ করুন',
    'footer.address': 'ঠিকানা',
    'footer.phone': 'ফোন',
    'footer.email': 'ইমেইল',
    'footer.follow_us': 'আমাদের অনুসরণ করুন',
    'footer.copyright': '© 2024 বিকাশ ফান্ড। সর্বস্বত্ব সংরক্ষিত।',
    
    // Admin Panel
    'admin.dashboard': 'ড্যাশবোর্ড',
    'admin.applications': 'আবেদন',
    'admin.success_stories': 'সাফল্যের গল্প',
    'admin.funding_options': 'ফান্ডিং অপশন',
    'admin.blog_posts': 'ব্লগ পোস্ট',
    'admin.faqs': 'সাধারণ প্রশ্ন',
    'admin.settings': 'সেটিংস',
    'admin.masters': 'মাস্টার',
    'admin.country_master': 'দেশ মাস্টার',
    'admin.state_master': 'রাজ্য মাস্টার',
    'admin.city_master': 'শহর মাস্টার',
    'admin.business_category': 'ব্যবসার বিভাগ',
    'admin.application_periods': 'আবেদনের সময়কাল',
    'admin.funding_results': 'ফান্ডিং ফলাফল',
    'admin.logout': 'লগআউট',
    
    // Common
    'common.add': 'যোগ করুন',
    'common.edit': 'সম্পাদনা করুন',
    'common.delete': 'মুছুন',
    'common.save': 'সংরক্ষণ করুন',
    'common.cancel': 'বাতিল করুন',
    'common.search': 'অনুসন্ধান করুন',
    'common.filter': 'ফিল্টার',
    'common.actions': 'কর্ম',
    'common.status': 'অবস্থা',
    'common.created_at': 'তৈরি হয়েছে',
    'common.updated_at': 'আপডেট হয়েছে',
    'common.loading': 'লোড হচ্ছে...',
    'common.no_data': 'কোন ডেটা নেই',
    'common.success': 'সফল',
    'common.error': 'ত্রুটি',
    'common.warning': 'সতর্কতা',
    'common.info': 'তথ্য'
  }
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // Get language from localStorage or default to English
    const savedLanguage = localStorage.getItem('selectedLanguage');
    return savedLanguage || 'EN';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('selectedLanguage', currentLanguage);
  }, [currentLanguage]);

  const changeLanguage = (languageCode) => {
    setCurrentLanguage(languageCode);
  };

  const t = (key) => {
    return translations[currentLanguage]?.[key] || translations['EN'][key] || key;
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    translations
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 